import { CheckCircle, XCircle, Loader2, X } from 'lucide-react';
import type { UploadQueueItem, UploadProgress } from '../../hooks/useUploadQueue';

interface UploadProgressBarProps {
  queue: UploadQueueItem[];
  progress: UploadProgress;
  isUploading: boolean;
  onClear: () => void;
}

export function UploadProgressBar({
  queue,
  progress,
  isUploading,
  onClear,
}: UploadProgressBarProps) {
  if (queue.length === 0) return null;

  const pct =
    progress.total > 0 ? ((progress.completed + progress.failed) / progress.total) * 100 : 0;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-700">
          {isUploading
            ? `Uploading ${progress.completed + 1} of ${progress.total}...`
            : progress.failed > 0
              ? `Done — ${progress.completed} uploaded, ${progress.failed} failed`
              : `${progress.completed} photo${progress.completed !== 1 ? 's' : ''} uploaded`}
        </p>
        {!isUploading && (
          <button onClick={onClear} className="text-gray-400 hover:text-gray-600" title="Dismiss">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            progress.failed > 0 ? 'bg-amber-500' : 'bg-primary-600'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* File list (show max 5, then collapse) */}
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {queue.map((item) => (
          <div key={item.id} className="flex items-center gap-2 text-xs">
            {item.status === 'uploading' && (
              <Loader2 size={12} className="text-primary-600 animate-spin flex-shrink-0" />
            )}
            {item.status === 'done' && (
              <CheckCircle size={12} className="text-green-500 flex-shrink-0" />
            )}
            {item.status === 'error' && (
              <XCircle size={12} className="text-red-500 flex-shrink-0" />
            )}
            {item.status === 'pending' && (
              <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
            )}
            <span
              className={`truncate flex-1 ${item.status === 'error' ? 'text-red-600' : 'text-gray-600'}`}
            >
              {item.fileName}
            </span>
            {item.status === 'uploading' && item.uploadPct != null && (
              <span className="text-primary-600 font-medium flex-shrink-0">{item.uploadPct}%</span>
            )}
            {item.error && <span className="text-red-500 flex-shrink-0">— {item.error}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
